import React, { useEffect, useState } from "react";
import api, { formatApiError, launchEnrollment } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { IntakeDetailDialog } from "@/components/IntakeDetailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Stethoscope, ArrowRight, ArrowLeft, Check, Loader2, CheckCircle2, XCircle,
  ExternalLink, Plus, UserCheck,
} from "lucide-react";

const PRODUCT_TYPES = [
  ["MAPD", "Medicare Advantage (MAPD)"],
  ["PDP", "Prescription Drug (PDP)"],
  ["SNP", "Special Needs Plan (SNP)"],
  ["MEDSUPP", "Medicare Supplement"],
];
const REL = { spouse: "Spouse", domestic_partner: "Partner", child: "Child", parent: "Parent", other_dependent: "Dependent" };
const STEPS = ["Who's this for", "Coverage & product", "Doctors & meds", "Choose advisor", "Hand-off"];

export default function ConsumerMedicare() {
  const { user } = useAuth();
  const [intakes, setIntakes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [lastIntakeId, setLastIntakeId] = useState(null);
  const [eligible, setEligible] = useState(null);
  const [chosen, setChosen] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [form, setForm] = useState(null);

  const emptyForm = () => {
    const parts = (user?.name || "").split(" ");
    return {
      household_member_id: "", subject_relationship: "self",
      consumer_first: parts[0] || "", consumer_last: parts.slice(1).join(" ") || "",
      date_of_birth: (user?.profile?.date_of_birth || "").slice(0, 10),
      zip_code: user?.profile?.zip_code || "", state: user?.profile?.state || "TX",
      product_type: "MAPD", has_part_a: false, has_part_b: false, existing_plan: "",
      coverage_effective_date: "", doctorsStr: "", medsStr: "",
    };
  };

  const load = () => api.get("/medicare/consumer/intakes").then((r) => setIntakes(r.data));
  useEffect(() => {
    Promise.all([load(), api.get("/household")])
      .then(([, hh]) => setMembers(hh.data.members || []))
      .finally(() => setLoading(false));
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const startNew = () => { setForm(emptyForm()); setStep(0); setResult(null); setEligible(null); setChosen(""); setWizard(true); };

  const onSubject = (v) => {
    if (v === "self") {
      const parts = (user?.name || "").split(" ");
      setForm((f) => ({ ...f, household_member_id: "", subject_relationship: "self",
        consumer_first: parts[0] || "", consumer_last: parts.slice(1).join(" ") || "",
        date_of_birth: (user?.profile?.date_of_birth || "").slice(0, 10) }));
    } else {
      const m = members.find((x) => x.id === v);
      if (m) setForm((f) => ({ ...f, household_member_id: v, subject_relationship: m.relationship,
        consumer_first: m.first_name, consumer_last: m.last_name,
        date_of_birth: (m.date_of_birth || "").slice(0, 10) }));
    }
  };

  const fetchEligible = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/medicare/eligible-producers", { state: form.state, product_type: form.product_type });
      setEligible(data.producers);
      if (data.producers.length === 1) setChosen(data.producers[0].producer_id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const goStep = async (next) => {
    if (next === 3) await fetchEligible();
    setStep(next);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        consumer_first: form.consumer_first, consumer_last: form.consumer_last,
        date_of_birth: form.date_of_birth, zip_code: form.zip_code || null, state: form.state,
        product_type: form.product_type, has_part_a: form.has_part_a, has_part_b: form.has_part_b,
        existing_plan: form.existing_plan || null, coverage_effective_date: form.coverage_effective_date || null,
        doctors: form.doctorsStr.split(",").map((s) => s.trim()).filter(Boolean),
        medications: form.medsStr.split(",").map((s) => s.trim()).filter(Boolean),
        producer_id: chosen,
        household_member_id: form.household_member_id || null,
        subject_relationship: form.subject_relationship || "self",
      };
      const { data } = await api.post("/medicare/consumer/intakes", payload);
      setResult(data.route);
      setLastIntakeId(data.intake.id);
      load();
      toast.success("Your enrollment route is ready");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-6 sm:p-12 max-w-4xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;

  const canNext = step === 0
    ? form?.consumer_first && form?.consumer_last && form?.date_of_birth && form?.state
    : step === 3 ? !!chosen : true;

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-4xl mx-auto animate-rise">
      <PageHeader
        eyebrow="Medicare"
        title="Explore your Medicare options"
        subtitle="Tell us who this is for and what you need — we'll connect you with an approved advisor to complete enrollment. No jargon, no pressure."
        action={!wizard && (
          <Button onClick={startNew} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="cm-start">
            <Plus className="w-4 h-4 mr-1" /> Start
          </Button>
        )}
      />

      {wizard && form && (
        <div className="bg-white rounded-2xl border border-mist-200 p-8 mb-8">
          <div className="flex items-center gap-2 mb-8 overflow-x-auto">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2 shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i < step ? "bg-green-600 text-white" : i === step ? "bg-gold text-navy-900" : "bg-mist-200 text-muted-foreground"}`}>
                    {i < step ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-sm font-semibold hidden md:block ${i <= step ? "text-navy-900" : "text-muted-foreground"}`}>{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 min-w-[12px] ${i < step ? "bg-green-600" : "bg-mist-200"}`} />}
              </React.Fragment>
            ))}
          </div>

          {step === 0 && (
            <div className="space-y-4" data-testid="cm-step-about">
              <div>
                <Label className="font-semibold">This Medicare request is for</Label>
                <Select value={form.household_member_id || "self"} onValueChange={onSubject}>
                  <SelectTrigger className="mt-1.5" data-testid="cm-subject"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself{user?.name ? ` (${user.name})` : ""}</SelectItem>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id} data-testid={`cm-subject-${m.id}`}>
                        {m.first_name} {m.last_name} — {REL[m.relationship] || m.relationship}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Add a spouse or parent under <span className="font-semibold">My Household</span> to start a request for them.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="font-semibold">First name</Label><Input value={form.consumer_first} onChange={upd("consumer_first")} className="mt-1.5" data-testid="cm-first" /></div>
                <div><Label className="font-semibold">Last name</Label><Input value={form.consumer_last} onChange={upd("consumer_last")} className="mt-1.5" data-testid="cm-last" /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label className="font-semibold">Date of birth</Label><Input type="date" value={form.date_of_birth} onChange={upd("date_of_birth")} className="mt-1.5" data-testid="cm-dob" /></div>
                <div><Label className="font-semibold">ZIP</Label><Input value={form.zip_code} onChange={upd("zip_code")} className="mt-1.5" data-testid="cm-zip" /></div>
                <div><Label className="font-semibold">State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} maxLength={2} className="mt-1.5" data-testid="cm-state" /></div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="cm-step-coverage">
              <div>
                <Label className="font-semibold">What are you interested in?</Label>
                <Select value={form.product_type} onValueChange={(v) => setForm({ ...form, product_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="cm-product"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRODUCT_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="cm-parta"><Checkbox checked={form.has_part_a} onCheckedChange={(c) => setForm({ ...form, has_part_a: !!c })} /> <span className="text-sm font-semibold">Has Medicare Part A</span></label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="cm-partb"><Checkbox checked={form.has_part_b} onCheckedChange={(c) => setForm({ ...form, has_part_b: !!c })} /> <span className="text-sm font-semibold">Has Medicare Part B</span></label>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="font-semibold">Current plan <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={form.existing_plan} onChange={upd("existing_plan")} className="mt-1.5" data-testid="cm-existing" /></div>
                <div><Label className="font-semibold">Coverage start date</Label><Input type="date" value={form.coverage_effective_date} onChange={upd("coverage_effective_date")} className="mt-1.5" data-testid="cm-effective" /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="cm-step-doctors">
              <p className="text-sm text-muted-foreground">Optional — this helps your advisor prepare. It's stored securely and shared only when you enroll.</p>
              <div><Label className="font-semibold">Doctors (comma separated)</Label><Input value={form.doctorsStr} onChange={upd("doctorsStr")} className="mt-1.5" placeholder="Dr. Smith, Dr. Lee" data-testid="cm-doctors" /></div>
              <div><Label className="font-semibold">Medications (comma separated)</Label><Input value={form.medsStr} onChange={upd("medsStr")} className="mt-1.5" placeholder="Atorvastatin, Metformin" data-testid="cm-meds" /></div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="cm-step-advisor">
              <h3 className="font-display text-lg font-bold text-navy-900 mb-1">Confirm your advisor</h3>
              <p className="text-sm text-muted-foreground mb-5">These advisors are approved to help with {form.product_type} in {form.state}.</p>
              {busy ? <Skeleton className="h-24 w-full" /> : eligible && eligible.length ? (
                <div className="space-y-3">
                  {eligible.map((p) => (
                    <label key={p.producer_id} data-testid={`cm-advisor-${p.producer_id}`}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${chosen === p.producer_id ? "border-gold bg-gold/5" : "border-mist-200 hover:border-mist-300"}`}>
                      <input type="radio" name="advisor" checked={chosen === p.producer_id} onChange={() => setChosen(p.producer_id)} className="accent-gold w-4 h-4" />
                      <div className="w-10 h-10 rounded-full bg-navy-800 text-gold flex items-center justify-center"><UserCheck className="w-5 h-5" /></div>
                      <div className="flex-1">
                        <p className="font-semibold text-navy-900">{p.name}</p>
                        <p className="text-xs text-muted-foreground">Enrollment via {p.provider}{p.npn ? ` · NPN ${p.npn}` : ""}</p>
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-5 text-sm text-amber-900" data-testid="cm-no-advisors">
                  No approved advisors are currently available for {form.product_type} in {form.state}. Please try a different product or check back later.
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div data-testid="cm-step-handoff">
              {!result ? (
                <div className="text-center py-6">
                  <h3 className="font-display text-xl font-bold text-navy-900 mb-2">Ready when you are</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">We'll connect you to your chosen advisor's secure enrollment page to finish.</p>
                  <Button onClick={submit} disabled={busy || !chosen} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="cm-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue to enrollment"}
                  </Button>
                </div>
              ) : (
                <ConsumerResult result={result} onDone={() => { setWizard(false); setResult(null); }}
                  onLaunch={() => launchEnrollment(lastIntakeId, result.destination?.url)} />
              )}
            </div>
          )}

          {!(step === 4 && result) && (
            <div className="flex justify-between mt-8 pt-6 border-t border-mist-200">
              <Button variant="outline" className="rounded-full" onClick={() => (step === 0 ? setWizard(false) : setStep(step - 1))} data-testid="cm-back">
                {step === 0 ? "Cancel" : (<><ArrowLeft className="w-4 h-4 mr-1" /> Back</>)}
              </Button>
              {step < 4 && (
                <Button disabled={!canNext} onClick={() => goStep(step + 1)} className="bg-navy-800 text-white hover:bg-navy-700 rounded-full font-bold" data-testid="cm-next">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <h3 className="font-display text-xl font-bold text-navy-900 mb-4">Your Medicare requests</h3>
      <div className="space-y-3">
        {intakes.length ? intakes.map((it) => (
          <div key={it.id} className="bg-white rounded-xl border border-mist-200 p-5 flex items-center gap-4" data-testid={`cm-intake-${it.id}`}>
            <div className="w-11 h-11 rounded-lg bg-mist-100 flex items-center justify-center shrink-0"><Stethoscope className="w-5 h-5 text-navy-800" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-navy-900">{it.consumer_first} {it.consumer_last} · {it.product_type} · {it.state}</p>
              <p className="text-sm text-muted-foreground">
                {it.subject_relationship && it.subject_relationship !== "self" ? `${REL[it.subject_relationship] || it.subject_relationship} · ` : ""}
                {it.route?.resolved ? `Routed to ${it.route.provider?.name}` : "Awaiting route"}
              </p>
            </div>
            <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border ${it.status === "routed" ? "bg-green-100 text-green-700 border-green-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{it.status === "routed" ? "Ready" : "Intake"}</span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setDetailId(it.id)} data-testid={`cm-view-${it.id}`}>View</Button>
          </div>
        )) : (
          <div className="bg-white rounded-xl border border-dashed border-mist-300 p-12 text-center text-muted-foreground" data-testid="cm-no-intakes">
            No Medicare requests yet. Tap Start to explore your options.
          </div>
        )}
      </div>

      <IntakeDetailDialog open={!!detailId} intakeId={detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}

function ConsumerResult({ result, onDone, onLaunch }) {
  return (
    <div data-testid="cm-result" className="text-center py-4">
      {result.resolved ? (
        <div className="space-y-4">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto"><CheckCircle2 className="w-7 h-7 text-green-600" /></div>
          <h3 className="font-display text-2xl font-bold text-navy-900">You're all set</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">Your advisor's secure {result.provider?.name} enrollment page is ready. Your information stays with you until you choose to continue.</p>
          <div className="flex gap-3 justify-center">
            {result.destination?.url && (
              <Button onClick={onLaunch} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="cm-launch">
                Continue to enrollment <ExternalLink className="w-4 h-4 ml-1" />
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={onDone} data-testid="cm-done">Done</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2 text-destructive font-semibold"><XCircle className="w-5 h-5" /> No route available</div>
          <p className="text-sm text-muted-foreground">{result.reason}</p>
          <Button variant="outline" className="rounded-full" onClick={onDone} data-testid="cm-done">Done</Button>
        </div>
      )}
    </div>
  );
}
